import { COLORS, FONTS } from '../styles/tokens';
import { AUTH_METHODS, formatDate } from '../constants/auth-methods';
import type { EnrichedCredential } from '../hooks/useUserCredentials';

interface ThemeColors {
  text: string;
  textSecondary: string;
  border: string;
}

interface CredentialListProps {
  credentials: EnrichedCredential[];
  loading: boolean;
  onRemove: (credentialId: string) => void;
  isDark: boolean;
  colors: ThemeColors;
}

/** Displays the list of configured authentication credentials */
export function CredentialList({ credentials, loading, onRemove, isDark, colors }: CredentialListProps): React.JSX.Element {
  return (
    <div style={{ marginBottom: '28px' }}>
      <h3 style={{
        fontFamily: FONTS.heading,
        fontSize: '11px',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        color: colors.textSecondary,
        margin: '0 0 12px',
      }}>
        CONFIGURED METHODS
      </h3>

      {loading && (
        <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary }}>
          Loading credentials...
        </p>
      )}

      {!loading && credentials.length === 0 && (
        <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary }}>
          No authentication methods configured yet.
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {credentials.map(cred => (
          <div
            key={cred.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
              border: `1px solid ${colors.border}`,
            }}
          >
            {cred.method && (
              <img
                src={cred.method.icon}
                alt=""
                style={{
                  width: '28px',
                  height: '28px',
                  filter: isDark ? 'none' : 'invert(1)',
                  opacity: 0.8,
                }}
              />
            )}

            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: '13px',
                fontWeight: 600,
                color: colors.text,
                margin: 0,
              }}>
                {cred.method?.label ?? cred.type}
              </p>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: '11px',
                color: colors.textSecondary,
                margin: '2px 0 0',
              }}>
                Added {formatDate(cred.createdDate)}
              </p>
            </div>

            <button
              onClick={() => onRemove(cred.id)}
              style={{
                background: 'none',
                border: `1px solid rgba(239, 68, 68, 0.3)`,
                borderRadius: '6px',
                padding: '4px 10px',
                cursor: 'pointer',
                fontFamily: FONTS.heading,
                fontSize: '10px',
                letterSpacing: '0.1em',
                color: '#EF4444',
              }}
            >
              REMOVE
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

interface AddMethodCardsProps {
  onAdd: (methodId: string) => void;
  isDark: boolean;
  colors: ThemeColors;
}

/** Displays cards for each available auth method the user can add */
export function AddMethodCards({ onAdd, isDark, colors }: AddMethodCardsProps): React.JSX.Element {
  return (
    <div>
      <h3 style={{
        fontFamily: FONTS.heading,
        fontSize: '11px',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        color: colors.textSecondary,
        margin: '0 0 12px',
      }}>
        ADD METHOD
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {AUTH_METHODS.map(method => (
          <button
            key={method.id}
            onClick={() => onAdd(method.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '14px',
              borderRadius: '8px',
              backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
              border: `1px solid ${colors.border}`,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'border-color 0.2s',
            }}
          >
            <img
              src={method.icon}
              alt=""
              style={{
                width: '32px',
                height: '32px',
                filter: isDark ? 'none' : 'invert(1)',
                opacity: 0.8,
              }}
            />
            <div>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: '13px',
                fontWeight: 600,
                color: colors.text,
                margin: 0,
              }}>
                {method.label}
              </p>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: '11px',
                color: colors.textSecondary,
                margin: '2px 0 0',
              }}>
                {method.description}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
